const restore = ({ element, style }) => {
  if (style === null) element.removeAttribute('style');
  else element.setAttribute('style', style);
};
export { restore as restoreElementStyle };

// A transform establishes a containing block. Preserve positioned descendants
// before applying frame motion, including containers that were originally static.
export function prepareMotionLayout(elements, effects) {
  for (const baseline of elements.values()) restore(baseline);
  const targets = [...elements.entries()].filter(([id, baseline]) => !baseline.inline && [effects.get(id), ...Object.values(typeof effects.get(id) === 'object' && effects.get(id) || {})].some(preset => ['slide-left', 'zoom-in', 'soft-float'].includes(preset)));
  const positioned = [...elements.values()].filter(({ element }) => {
    const position = element.ownerDocument.defaultView.getComputedStyle(element).position;
    return ['absolute', 'fixed'].includes(position) && targets.some(([, target]) => target.element !== element && target.element.contains(element));
  }).map(baseline => {
    const computed = baseline.element.ownerDocument.defaultView.getComputedStyle(baseline.element);
    return { ...baseline, rect: baseline.element.getBoundingClientRect(), width: computed.width, height: computed.height, spacing: Object.fromEntries(['padding-left','padding-right','padding-top','padding-bottom','margin-left','margin-right','margin-top','margin-bottom'].map(property => [property, computed.getPropertyValue(property)])) };
  });
  for (const [, baseline] of targets) {
    baseline.element.style.setProperty('transform', baseline.transform === 'none' ? 'matrix(1,0,0,1,0,0)' : baseline.transform, 'important');
  }
  for (const { element, rect, width, height, spacing } of positioned) {
    const current = element.getBoundingClientRect();
    if (Math.abs(current.x - rect.x) < .001 && Math.abs(current.y - rect.y) < .001 && Math.abs(current.width - rect.width) < .001 && Math.abs(current.height - rect.height) < .001) continue;
    for (const [property, value] of Object.entries(spacing)) element.style.setProperty(property, value, 'important');
    for (const axis of ['width', 'height']) {
      element.style.setProperty(`min-${axis}`, '0px', 'important');
      element.style.setProperty(`max-${axis}`, 'none', 'important');
    }
    element.style.setProperty('width', width, 'important');
    element.style.setProperty('height', height, 'important');
    const computed = element.ownerDocument.defaultView.getComputedStyle(element);
    let left = parseFloat(computed.left), top = parseFloat(computed.top);
    if (!Number.isFinite(left)) left = element.offsetLeft - (parseFloat(computed.marginLeft) || 0);
    if (!Number.isFinite(top)) top = element.offsetTop - (parseFloat(computed.marginTop) || 0);
    element.style.setProperty('right', 'auto', 'important');
    element.style.setProperty('bottom', 'auto', 'important');
    const move = (x, y) => {
      element.style.setProperty('left', `${x}px`, 'important');
      element.style.setProperty('top', `${y}px`, 'important');
      return element.getBoundingClientRect();
    };
    // Measure the containing block's screen-space basis; this also handles rotated
    // or scaled ancestors without guessing from bounding-box width/height ratios.
    const origin = move(left, top), x = move(left + 1, top), y = move(left, top + 1);
    const a = x.x - origin.x, b = x.y - origin.y, c = y.x - origin.x, d = y.y - origin.y;
    const determinant = a * d - b * c;
    if (Math.abs(determinant) > 1e-8) {
      const dx = rect.x - origin.x, dy = rect.y - origin.y;
      move(left + (d * dx - c * dy) / determinant, top + (a * dy - b * dx) / determinant);
    } else move(left, top);
  }
  return new Map([...elements].map(([id, baseline]) => [id, { ...baseline, style: baseline.element.getAttribute('style') }]));
}
